import { z } from 'zod';
import { api } from '@shared/api';

export const stockSyncSettingsSchema = z.object({
  data: z.object({
    updatesEnabled: z.boolean(),
    scheduleEnabled: z.boolean(),
    intervalMinutes: z.number().int().min(15).max(1440).nullable(),
    itemLimit: z.number().int().min(1).max(1000).nullable(),
    nextRunAt: z.iso.datetime({ offset: true }).nullable(),
    manualRunAvailable: z.boolean(),
    reason: z.string().nullable(),
    revision: z.number().int().nonnegative(),
  }),
});
const strongEtagSchema = z.string().regex(/^"[^"\r\n]+"$/u);
const count = z.number().int().nonnegative();
const runSchema = z.object({
  id: z.string().min(1),
  status: z.enum(['pending', 'running', 'completed', 'stopped', 'reconciliation_required']),
  createdAt: z.iso.datetime({ offset: true }),
  completedAt: z.iso.datetime({ offset: true }).nullable(),
  locationId: z.string().min(1),
  warehouseId: z.string().min(1),
  updatedSkus: count,
  updatedArticles: count,
  unchangedSkus: count,
  conflictSkus: count,
  rejectedSkus: count,
  unknownSkus: count,
});
export const stockSyncRunsSchema = z.object({
  data: z.object({ runs: z.array(runSchema).max(10), limit: z.number().int().min(1).max(10) }),
});
export type StockSyncSettings = z.infer<typeof stockSyncSettingsSchema>['data'] & { etag: string };
export type StockSyncRun = z.infer<typeof runSchema>;
export type StockSyncRuns = z.infer<typeof stockSyncRunsSchema>['data'];

export async function getStockSyncSettings(signal: AbortSignal): Promise<StockSyncSettings> {
  const response = await api.getWithMetadata('/api/v1/shopify/stock-sync/settings', {
    signal,
    schema: stockSyncSettingsSchema,
  });
  return {
    ...stockSyncSettingsSchema.parse(response.data).data,
    etag: strongEtagSchema.parse(response.headers.get('etag')),
  };
}

export async function saveStockSyncInterval(
  intervalMinutes: number,
  etag: string
): Promise<StockSyncSettings> {
  return saveStockSyncSettings({ intervalMinutes }, etag);
}

export async function saveStockSyncSettings(
  patch: { intervalMinutes?: number; scheduleEnabled?: boolean; itemLimit?: number | null },
  etag: string
): Promise<StockSyncSettings> {
  const body = z
    .strictObject({
      intervalMinutes: z.number().int().min(15).max(1440).optional(),
      scheduleEnabled: z.boolean().optional(),
      itemLimit: z.number().int().min(1).max(1000).nullable().optional(),
    })
    .refine((value) => Object.keys(value).length > 0)
    .parse(patch);
  const validator = strongEtagSchema.parse(etag);
  const response = await api.patchWithMetadata('/api/v1/shopify/stock-sync/settings', body, {
    headers: {
      'If-Match': validator,
      'Content-Type': 'application/merge-patch+json',
    },
    schema: stockSyncSettingsSchema,
  });
  return {
    ...stockSyncSettingsSchema.parse(response.data).data,
    etag: strongEtagSchema.parse(response.headers.get('etag')),
  };
}

const receiptSchema = z.object({
  data: z.object({
    id: z.uuid(),
    status: z.literal('pending'),
    acceptedAt: z.iso.datetime({ offset: true }),
    monitorUrl: z.string(),
  }),
});
const monitorSchema = z.object({
  data: z.object({
    id: z.uuid(),
    status: z.enum(['pending', 'running', 'completed', 'stopped', 'reconciliation_required']),
    trigger: z.enum(['manual', 'scheduled']),
    acceptedAt: z.iso.datetime({ offset: true }),
    startedAt: z.iso.datetime({ offset: true }).nullable(),
    completedAt: z.iso.datetime({ offset: true }).nullable(),
    locationCount: count,
    completedLocationCount: count,
  }),
});
export type StockSyncReceipt = z.infer<typeof receiptSchema>['data'];
export type StockSyncMonitor = z.infer<typeof monitorSchema>['data'];
export interface StockSyncItemTarget {
  inventoryItemId: string;
  locationId: string;
}

export async function requestStockSyncItemRun(
  target: StockSyncItemTarget,
  idempotencyKey: string
): Promise<StockSyncReceipt> {
  const body = z
    .strictObject({
      inventoryItemId: z
        .string()
        .regex(/^gid:\/\/shopify\/InventoryItem\/[1-9][0-9]*$/u)
        .max(100),
      locationId: z
        .string()
        .regex(/^gid:\/\/shopify\/Location\/[1-9][0-9]*$/u)
        .max(100),
    })
    .parse(target);
  const key = z.string().min(1).max(200).trim().parse(idempotencyKey);
  const response = receiptSchema.parse(
    await api.post('/api/v1/shopify/stock-sync/item-runs', body, {
      headers: { 'Idempotency-Key': key },
    })
  ).data;
  if (response.monitorUrl !== `/api/v1/shopify/stock-sync/runs/${response.id}`)
    throw new Error('Invalid stock synchronization monitor');
  return response;
}

export async function requestStockSyncRun(
  idempotencyKey: string,
  etag?: string
): Promise<StockSyncReceipt> {
  const key = z.string().min(1).max(200).trim().parse(idempotencyKey);
  const response = receiptSchema.parse(
    await api.post(
      '/api/v1/shopify/stock-sync/runs',
      {},
      {
        headers: {
          'Idempotency-Key': key,
          ...(etag === undefined ? {} : { 'If-Match': strongEtagSchema.parse(etag) }),
        },
      }
    )
  ).data;
  if (response.monitorUrl !== `/api/v1/shopify/stock-sync/runs/${response.id}`)
    throw new Error('Invalid stock synchronization monitor');
  return response;
}

export async function getStockSyncRun(id: string, signal: AbortSignal): Promise<StockSyncMonitor> {
  const runId = z.uuid().parse(id);
  const response = monitorSchema.parse(
    await api.get(`/api/v1/shopify/stock-sync/runs/${runId}`, { signal })
  ).data;
  if (response.id !== runId) throw new Error('Invalid stock synchronization request identity');
  return response;
}

export async function getStockSyncRuns(signal: AbortSignal): Promise<StockSyncRuns> {
  return stockSyncRunsSchema.parse(
    await api.get('/api/v1/shopify/stock-sync/runs', { signal, params: { limit: 10 } })
  ).data;
}
